import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  FileText,
  Calendar,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Check,
  AlertCircle,
} from 'lucide-react';
import { useThemeStore } from '../../store/theme';
import type { ConnectorProvider, ConnectorAction } from '../../types';

interface ConnectorResultCardProps {
  provider: ConnectorProvider;
  providerName: string;
  action: ConnectorAction;
  success: boolean;
  data?: unknown;
  error?: string;
}

// Provider icons mapping
const PROVIDER_ICONS: Record<ConnectorProvider, typeof Mail> = {
  gmail: Mail,
  google_docs: FileText,
  google_calendar: Calendar,
  google_drive: FolderOpen,
  trello: FileText,
};

// Provider colors
const PROVIDER_COLORS: Record<ConnectorProvider, { bg: string; text: string; border: string }> = {
  gmail: { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/30' },
  google_docs: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/30' },
  google_calendar: { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/30' },
  google_drive: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/30' },
  trello: { bg: 'bg-indigo-500/10', text: 'text-indigo-500', border: 'border-indigo-500/30' },
};

// Action labels
const ACTION_LABELS: Record<string, string> = {
  read_emails: 'Emails',
  search_emails: 'Search Results',
  get_labels: 'Labels',
  read_events: 'Calendar Events',
  list_calendars: 'Calendars',
  read_doc: 'Document',
  list_docs: 'Documents',
  list_files: 'Files',
  search_files: 'Search Results',
  download_file: 'File Content',
  get_file_info: 'File Info',
  list_boards: 'Boards',
  list_lists: 'Lists',
  list_cards: 'Cards',
};

export default function ConnectorResultCard({
  provider,
  providerName,
  action,
  success,
  data,
  error,
}: ConnectorResultCardProps) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [isExpanded, setIsExpanded] = useState(true);

  const Icon = PROVIDER_ICONS[provider] || Mail;
  const colors = PROVIDER_COLORS[provider] || PROVIDER_COLORS.gmail;
  const actionLabel = ACTION_LABELS[action] || action;

  // Format data for display
  const renderData = () => {
    if (!data) return null;

    if (Array.isArray(data)) {
      if (data.length === 0) {
        return (
          <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
            No results found
          </p>
        );
      }

      // Render as a list of items
      return (
        <div className="space-y-2">
          {data.slice(0, 10).map((item, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg ${
                isDark ? 'bg-dark-800' : 'bg-slate-50'
              }`}
            >
              {renderItem(item)}
            </div>
          ))}
          {data.length > 10 && (
            <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              + {data.length - 10} more items
            </p>
          )}
        </div>
      );
    }

    if (typeof data === 'object' && data !== null) {
      return <div className="space-y-2">{renderItem(data)}</div>;
    }

    return (
      <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-600'}`}>
        {String(data)}
      </p>
    );
  };

  // Render a single item based on its structure
  const renderItem = (item: unknown) => {
    if (!item || typeof item !== 'object') {
      return <span>{String(item)}</span>;
    }

    const obj = item as Record<string, unknown>;

    // Email item
    if ('subject' in obj || 'from' in obj) {
      const subject = obj.subject as string | undefined;
      const from = obj.from as string | undefined;
      const date = obj.date as string | undefined;
      const snippet = obj.snippet as string | undefined;
      return (
        <div>
          <div className="flex items-center justify-between gap-2">
            <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {subject || 'No Subject'}
            </p>
            {date && (
              <span className={`text-xs shrink-0 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                {new Date(date).toLocaleDateString()}
              </span>
            )}
          </div>
          {from && (
            <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              From: {from}
            </p>
          )}
          {snippet && (
            <p className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-text-muted' : 'text-slate-600'}`}>
              {snippet}
            </p>
          )}
        </div>
      );
    }

    // Calendar event
    if ('title' in obj && ('start' in obj || 'startTime' in obj)) {
      const title = obj.title as string;
      const start = (obj.start || obj.startTime) as string | undefined;
      const end = (obj.end || obj.endTime) as string | undefined;
      const location = obj.location as string | undefined;
      return (
        <div>
          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {title}
          </p>
          <div className={`flex items-center gap-2 text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
            <Calendar className="w-3 h-3" />
            <span>
              {start && new Date(start).toLocaleString()}
              {end && ` - ${new Date(end).toLocaleTimeString()}`}
            </span>
          </div>
          {location && (
            <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              Location: {location}
            </p>
          )}
        </div>
      );
    }

    // File/Document item
    if ('name' in obj && ('mimeType' in obj || 'type' in obj)) {
      const name = obj.name as string;
      const modifiedTime = obj.modifiedTime as string | undefined;
      const webViewLink = obj.webViewLink as string | undefined;
      return (
        <div className="flex items-center gap-2">
          <FileText className={`w-4 h-4 shrink-0 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
          <div className="min-w-0">
            <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {name}
            </p>
            {modifiedTime && (
              <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                Modified: {new Date(modifiedTime).toLocaleDateString()}
              </p>
            )}
          </div>
          {webViewLink && (
            <a
              href={webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`shrink-0 p-1 rounded hover:bg-dark-600 ${colors.text}`}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      );
    }

    // Trello board/card
    if ('name' in obj && ('url' in obj || 'idBoard' in obj)) {
      const name = obj.name as string;
      const description = obj.description as string | undefined;
      const url = obj.url as string | undefined;
      return (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {name}
            </p>
            {description && (
              <p className={`text-xs mt-1 line-clamp-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                {description}
              </p>
            )}
          </div>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={`shrink-0 p-1 rounded hover:bg-dark-600 ${colors.text}`}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      );
    }

    // Generic object
    return (
      <div className="space-y-1">
        {Object.entries(obj).slice(0, 5).map(([key, value]) => (
          <div key={key} className="flex gap-2">
            <span className={`text-xs font-medium capitalize ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              {key.replace(/_/g, ' ')}:
            </span>
            <span className={`text-xs ${isDark ? 'text-white' : 'text-slate-700'}`}>
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border overflow-hidden ${
        isDark
          ? 'bg-dark-700 border-dark-600'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      {/* Header */}
      <div
        className={`flex items-center gap-3 p-3 cursor-pointer ${
          isDark ? 'hover:bg-dark-600' : 'hover:bg-slate-50'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bg}`}>
          <Icon className={`w-4 h-4 ${colors.text}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {providerName}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
              {actionLabel}
            </span>
            {success ? (
              <span className="flex items-center gap-1 text-xs text-green-500">
                <Check className="w-3 h-3" />
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-red-500">
                <AlertCircle className="w-3 h-3" />
              </span>
            )}
          </div>
        </div>

        <button className={`p-1 rounded ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className={`border-t ${isDark ? 'border-dark-600' : 'border-slate-200'}`}
        >
          <div className="p-3 max-h-80 overflow-y-auto">
            {error ? (
              <div className={`p-3 rounded-lg ${
                isDark ? 'bg-red-500/10' : 'bg-red-50'
              }`}>
                <p className="text-sm text-red-500">{error}</p>
              </div>
            ) : (
              renderData()
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
